import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import EmailGateModal from "@/components/EmailGateModal";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, BarChart3, TrendingUp, TrendingDown,
  Check, AlertTriangle, X, Target, Users, Mail,
  ChevronDown, ChevronUp, Zap, Award, Minus,
} from "lucide-react";

// ── Benchmark data by industry ──
const BENCHMARKS: Record<string, { label: string; openRate: number; clickRate: number; bounceRate: number; unsubRate: number; listGrowth: number }> = {
  ecommerce:      { label: "E-commerce / Retail",   openRate: 15.7, clickRate: 2.1, bounceRate: 0.6, unsubRate: 0.2, listGrowth: 3.5 },
  saas:           { label: "SaaS / Technology",     openRate: 21.3, clickRate: 2.8, bounceRate: 0.5, unsubRate: 0.3, listGrowth: 4.2 },
  real_estate:    { label: "Real Estate",           openRate: 19.2, clickRate: 1.9, bounceRate: 0.7, unsubRate: 0.2, listGrowth: 2.8 },
  health:         { label: "Health & Wellness",     openRate: 20.5, clickRate: 2.4, bounceRate: 0.4, unsubRate: 0.2, listGrowth: 3.8 },
  education:      { label: "Education / Courses",   openRate: 22.1, clickRate: 2.6, bounceRate: 0.4, unsubRate: 0.3, listGrowth: 5.1 },
  agency:         { label: "Agency / B2B",          openRate: 18.8, clickRate: 2.2, bounceRate: 0.6, unsubRate: 0.3, listGrowth: 3.0 },
  local_services: { label: "Local Services",        openRate: 20.1, clickRate: 2.0, bounceRate: 0.5, unsubRate: 0.2, listGrowth: 2.5 },
  nonprofit:      { label: "Nonprofit",             openRate: 24.5, clickRate: 2.9, bounceRate: 0.3, unsubRate: 0.1, listGrowth: 2.2 },
  media:          { label: "Media / Publishing",    openRate: 22.0, clickRate: 3.2, bounceRate: 0.3, unsubRate: 0.2, listGrowth: 4.5 },
  finance:        { label: "Finance / Insurance",   openRate: 19.6, clickRate: 2.1, bounceRate: 0.5, unsubRate: 0.2, listGrowth: 2.8 },
  general:        { label: "Other / General",       openRate: 19.5, clickRate: 2.3, bounceRate: 0.5, unsubRate: 0.2, listGrowth: 3.2 },
};

interface MetricComparison {
  name: string;
  yourValue: number;
  benchmark: number;
  unit: string;
  higherIsBetter: boolean;
  status: "above" | "at" | "below";
  pctDiff: number;
  tip: string;
}

function compareMetric(
  name: string, yours: number, bench: number, unit: string,
  higherIsBetter: boolean, tip: string
): MetricComparison {
  const diff = yours - bench;
  const pctDiff = bench > 0 ? Math.round((diff / bench) * 100) : 0;
  const threshold = bench * 0.1; // within 10% is "at"

  let status: "above" | "at" | "below";
  if (higherIsBetter) {
    status = diff > threshold ? "above" : diff < -threshold ? "below" : "at";
  } else {
    status = diff < -threshold ? "above" : diff > threshold ? "below" : "at";
  }

  return { name, yourValue: yours, benchmark: bench, unit, higherIsBetter, status, pctDiff, tip };
}

function calcOverallGrade(metrics: MetricComparison[]): { grade: string; color: string; score: number } {
  let score = 50;
  for (const m of metrics) {
    if (m.status === "above") score += 12;
    else if (m.status === "at") score += 5;
    else score -= 8;
  }
  score = Math.max(0, Math.min(100, score));

  if (score >= 85) return { grade: "A", color: "text-emerald-600", score };
  if (score >= 70) return { grade: "B", color: "text-emerald-500", score };
  if (score >= 55) return { grade: "C", color: "text-amber-500", score };
  if (score >= 40) return { grade: "D", color: "text-orange-500", score };
  return { grade: "F", color: "text-red-500", score };
}

const STATUS_STYLE = {
  above: { icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200", label: "Above Average" },
  at:    { icon: Minus,      color: "text-amber-500",   bg: "bg-amber-50 border-amber-200",     label: "Average" },
  below: { icon: TrendingDown, color: "text-red-500",   bg: "bg-red-50 border-red-200",         label: "Below Average" },
};

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Newsletter Benchmark Report",
  applicationCategory: "BusinessApplication",
  description: "Compare your email marketing metrics to industry benchmarks. Get a personalized grade and improvement recommendations. Free tool — enter your stats and see how you stack up.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  operatingSystem: "Web",
};

export default function NewsletterBenchmarkPage() {
  const [industry, setIndustry] = useState("general");
  const [openRate, setOpenRate] = useState("");
  const [clickRate, setClickRate] = useState("");
  const [bounceRate, setBounceRate] = useState("");
  const [unsubRate, setUnsubRate] = useState("");
  const [listSize, setListSize] = useState("");
  const [showReport, setShowReport] = useState(false);

  const bench = BENCHMARKS[industry];

  const report = useMemo(() => {
    if (!showReport) return null;

    const or = parseFloat(openRate) || 0;
    const cr = parseFloat(clickRate) || 0;
    const br = parseFloat(bounceRate) || 0;
    const ur = parseFloat(unsubRate) || 0;

    const metrics: MetricComparison[] = [];

    if (openRate) {
      metrics.push(compareMetric("Open Rate", or, bench.openRate, "%", true,
        or < bench.openRate ? "Try A/B testing subject lines. Personalization and curiosity-driven subjects tend to lift opens." : "Your open rate is strong. Keep testing to maintain it."
      ));
    }
    if (clickRate) {
      metrics.push(compareMetric("Click Rate", cr, bench.clickRate, "%", true,
        cr < bench.clickRate ? "Make CTAs more prominent, reduce the number of links, and ensure content matches the subject line promise." : "Great click-through rate. Your content is resonating with readers."
      ));
    }
    if (bounceRate) {
      metrics.push(compareMetric("Bounce Rate", br, bench.bounceRate, "%", false,
        br > bench.bounceRate ? "Clean your list of invalid addresses. Use email verification before adding new subscribers." : "Your bounce rate is healthy. Keep validating new addresses."
      ));
    }
    if (unsubRate) {
      metrics.push(compareMetric("Unsubscribe Rate", ur, bench.unsubRate, "%", false,
        ur > bench.unsubRate ? "Review your sending frequency. Segment your list and send more targeted content." : "Low unsubscribe rate — your audience finds your content valuable."
      ));
    }

    if (metrics.length === 0) return null;

    const overall = calcOverallGrade(metrics);
    return { metrics, overall };
  }, [showReport, openRate, clickRate, bounceRate, unsubRate, bench]);

  function handleGenerate() {
    if (!openRate && !clickRate && !bounceRate && !unsubRate) return;
    setShowGate(true);
  }

  const [showGate, setShowGate] = useState(false);

  function handleGateComplete() {
    setShowGate(false);
    setShowReport(true);
  }

  function handleReset() {
    setShowReport(false);
    setOpenRate(""); setClickRate(""); setBounceRate(""); setUnsubRate("");
  }

  return (
    <>
      {showGate && (
        <EmailGateModal toolName="benchmark report" source="/newsletter-benchmark"
          onComplete={handleGateComplete} onClose={() => setShowGate(false)} />
      )}
      <SeoHead
        title="Newsletter Benchmark Report — Compare Your Email Metrics to Industry Averages | MoreMail"
        description="Free newsletter benchmark tool. Enter your open rate, click rate, and bounce rate. Get an instant comparison to industry averages with a personalized grade and improvement tips."
        canonical={`${SITE_URL}/newsletter-benchmark`}
        keywords="email benchmark report, newsletter benchmark, email marketing benchmarks, open rate comparison, click rate industry average, email marketing statistics, email performance benchmark"
        ogTitle="Newsletter Benchmark Report — How Do Your Emails Stack Up? | MoreMail"
        ogDescription="Enter your email metrics. See how you compare to your industry. Get a grade and personalized recommendations."
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-12 pb-6 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-5 backdrop-blur-sm">
            <Award size={13} className="text-coral" /> Free Tool
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            Newsletter <span className="text-coral">Benchmark Report</span>
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            Enter your email marketing metrics and see how you compare to industry averages.
            Get a personalized grade and improvement recommendations.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-6">

            {/* Input */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-sand p-5">
                <h2 className="font-display font-bold text-ink text-sm mb-4 flex items-center gap-2">
                  <BarChart3 size={15} className="text-coral" /> Your Metrics
                </h2>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-ink mb-1">Industry</label>
                    <select value={industry} onChange={(e) => { setIndustry(e.target.value); setShowReport(false); }}
                      className="w-full px-3 py-2 rounded-lg border border-sand text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral">
                      {Object.entries(BENCHMARKS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>

                  {[
                    { label: "Open Rate (%)", value: openRate, set: setOpenRate, placeholder: "e.g. 22.5", hint: "Percentage of recipients who opened" },
                    { label: "Click Rate (%)", value: clickRate, set: setClickRate, placeholder: "e.g. 3.1", hint: "Percentage who clicked a link" },
                    { label: "Bounce Rate (%)", value: bounceRate, set: setBounceRate, placeholder: "e.g. 0.8", hint: "Hard + soft bounces" },
                    { label: "Unsubscribe Rate (%)", value: unsubRate, set: setUnsubRate, placeholder: "e.g. 0.15", hint: "Per-campaign unsubscribe rate" },
                  ].map((field) => (
                    <div key={field.label}>
                      <label className="block text-xs font-semibold text-ink mb-1">{field.label}</label>
                      <input type="number" step="0.1" min="0" max="100"
                        value={field.value} onChange={(e) => { field.set(e.target.value); setShowReport(false); }}
                        placeholder={field.placeholder}
                        className="w-full px-3 py-2 rounded-lg border border-sand text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral" />
                      <p className="text-[10px] text-slate mt-0.5">{field.hint}</p>
                    </div>
                  ))}

                  <div className="flex gap-3 pt-1">
                    <button onClick={handleGenerate}
                      disabled={!openRate && !clickRate && !bounceRate && !unsubRate}
                      className="flex-1 flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white px-5 py-2.5 rounded-lg text-sm font-bold transition-colors disabled:opacity-50">
                      <Award size={15} /> Generate Report
                    </button>
                    {showReport && (
                      <button onClick={handleReset}
                        className="px-4 py-2.5 rounded-lg border border-sand text-sm font-medium text-slate hover:bg-paper transition-colors">
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Industry averages reference */}
              <div className="bg-white rounded-xl border border-sand p-5">
                <h3 className="text-xs font-bold text-ink mb-3 flex items-center gap-2">
                  <Target size={13} className="text-coral" /> {bench.label} Benchmarks
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Open Rate", value: `${bench.openRate}%` },
                    { label: "Click Rate", value: `${bench.clickRate}%` },
                    { label: "Bounce Rate", value: `${bench.bounceRate}%` },
                    { label: "Unsub Rate", value: `${bench.unsubRate}%` },
                  ].map(b => (
                    <div key={b.label} className="bg-paper rounded-lg px-3 py-2 text-center">
                      <p className="font-display font-bold text-ink text-sm">{b.value}</p>
                      <p className="text-[10px] text-slate">{b.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Report */}
            <div className="space-y-4">
              {!report ? (
                <div className="bg-white rounded-xl border border-sand p-8 text-center">
                  <Award size={40} className="mx-auto text-slate/20 mb-3" />
                  <p className="text-sm text-slate">Enter at least one metric and click "Generate Report" to see how you compare.</p>
                </div>
              ) : (
                <>
                  {/* Grade card */}
                  <div className="bg-white rounded-xl border border-sand p-5 flex items-center gap-5">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="#E5E7EB" strokeWidth="8" />
                        <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8"
                          className={report.overall.color.replace("text-", "stroke-")}
                          strokeDasharray={`${report.overall.score * 2.64} 264`}
                          strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`font-display font-bold text-3xl ${report.overall.color}`}>{report.overall.grade}</span>
                      </div>
                    </div>
                    <div>
                      <p className={`font-display font-bold text-lg ${report.overall.color}`}>
                        {report.overall.score >= 70 ? "Looking Good!" : report.overall.score >= 50 ? "Room to Improve" : "Needs Attention"}
                      </p>
                      <p className="text-xs text-slate mt-1">
                        {report.metrics.filter(m => m.status === "above").length} above average,{" "}
                        {report.metrics.filter(m => m.status === "at").length} at average,{" "}
                        {report.metrics.filter(m => m.status === "below").length} below average
                      </p>
                    </div>
                  </div>

                  {/* Metric comparisons */}
                  {report.metrics.map((m) => {
                    const style = STATUS_STYLE[m.status];
                    const Icon = style.icon;
                    return (
                      <div key={m.name} className={`rounded-xl border p-4 ${style.bg}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-bold text-ink">{m.name}</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold ${style.color}`}>{style.label}</span>
                            <Icon size={14} className={style.color} />
                          </div>
                        </div>

                        {/* Comparison bar */}
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex-1">
                            <div className="flex justify-between text-[10px] text-slate mb-1">
                              <span>You: <strong className="text-ink">{m.yourValue}{m.unit}</strong></span>
                              <span>Avg: <strong className="text-ink">{m.benchmark}{m.unit}</strong></span>
                            </div>
                            <div className="h-2 bg-white/60 rounded-full overflow-hidden relative">
                              {/* Benchmark marker */}
                              <div className="absolute top-0 bottom-0 w-0.5 bg-slate/40 z-10"
                                style={{ left: `${Math.min((m.benchmark / Math.max(m.yourValue, m.benchmark, 1)) * 100, 100)}%` }} />
                              {/* Your value bar */}
                              <div className={`h-full rounded-full transition-all duration-500 ${
                                m.status === "above" ? "bg-emerald-500" : m.status === "at" ? "bg-amber-400" : "bg-red-400"
                              }`}
                                style={{ width: `${Math.min((m.yourValue / Math.max(m.yourValue, m.benchmark, 1)) * 100, 100)}%` }} />
                            </div>
                          </div>
                          <span className={`text-xs font-bold ${style.color} min-w-[45px] text-right`}>
                            {m.pctDiff >= 0 ? "+" : ""}{m.pctDiff}%
                          </span>
                        </div>

                        <p className="text-xs text-slate leading-relaxed">{m.tip}</p>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* All benchmarks table */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-3">
            Email Marketing <span className="text-coral">Benchmarks by Industry</span>
          </h2>
          <p className="text-sm text-slate text-center mb-8 max-w-lg mx-auto">
            Average rates across thousands of campaigns. Use these as targets for your email program.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-sand">
                  <th className="text-left py-3 px-3 text-xs font-semibold text-slate">Industry</th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-slate">Open</th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-slate">Click</th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-slate">Bounce</th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-slate">Unsub</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(BENCHMARKS).map(([k, v]) => (
                  <tr key={k} className={`border-b border-sand/50 ${industry === k ? "bg-coral-light/30" : ""} hover:bg-paper cursor-pointer transition-colors`}
                    onClick={() => { setIndustry(k); setShowReport(false); }}>
                    <td className="py-2.5 px-3 font-medium text-ink text-xs">{v.label}</td>
                    <td className="py-2.5 px-3 text-right text-xs text-slate">{v.openRate}%</td>
                    <td className="py-2.5 px-3 text-right text-xs text-slate">{v.clickRate}%</td>
                    <td className="py-2.5 px-3 text-right text-xs text-slate">{v.bounceRate}%</td>
                    <td className="py-2.5 px-3 text-right text-xs text-slate">{v.unsubRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Beat the Benchmarks With MoreMail
          </h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">
            AI-powered subject lines, smart send times, engagement scoring, and deliverability tools
            designed to push every metric above the industry average.
          </p>
          <div className="flex justify-center mb-6">
            <LeadCaptureBox />
          </div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
